import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { 
  LayoutDashboard, Database, Search, Activity, LogOut, Users, Shield, 
  MessageCircle, Eye, Trophy, Megaphone, CalendarCheck, KeyRound, History, 
  Server, FileText, TrendingUp, UserPlus, CalendarOff, Wallet
} from 'lucide-react';
import { cn } from '../lib/utils';
import ChangePasswordDialog from './ChangePasswordDialog';
import ServerConfigDialog from './ServerConfigDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_467e7424-249d-4984-8afc-452f42a05c3c/artifacts/6ivxke97_Logo.png';

// Check if running in Tauri (desktop app)
const isDesktopApp = window.__TAURI__ !== undefined;

const Sidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [serverConfigOpen, setServerConfigOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const workerRef = useRef(null);

  // Poll unread chat count using Web Worker (immune to background tab throttling)
  const fetchUnread = useCallback(async () => {
    if (location.pathname === '/chat') { setUnreadChat(0); return; }
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${BACKEND_URL}/api/chat/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 8000
      });
      setUnreadChat(res.data.unread_messages || 0);
    } catch (e) {}
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    try {
      const w = new Worker('/poll-worker.js');
      workerRef.current = w;
      w.onmessage = () => fetchUnread();
      w.postMessage({ type: 'start', interval: 10000 });
    } catch (e) {
      const id = setInterval(fetchUnread, 5000);
      workerRef.current = { postMessage: () => {}, terminate: () => clearInterval(id) };
    }
    return () => {
      if (workerRef.current) { workerRef.current.postMessage({ type: 'stop' }); workerRef.current.terminate(); workerRef.current = null; }
    };
  }, [user, fetchUnread]);

  if (!user) return null;

  const adminLinks = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/today-sales', label: 'Today Sales', icon: TrendingUp },
    { path: '/data', label: 'Data Management', icon: Database },
    { path: '/search', label: 'Search Records', icon: Search },
    { path: '/chat', label: 'Chat', icon: MessageCircle },
    { path: '/attendance', label: 'Attendance', icon: CalendarCheck },
    { path: '/holidays', label: 'Holidays', icon: CalendarOff },
    { path: '/biometric-settings', label: 'Biometric', icon: Server },
    { path: '/users', label: 'User Management', icon: Users },
    { path: '/ip-management', label: 'IP Access Control', icon: Shield },
    { path: '/chat-monitor', label: 'Chat Monitoring', icon: Eye },
    { path: '/broadcast', label: 'Broadcast', icon: Megaphone },
    { path: '/new-joining', label: 'New Joining', icon: UserPlus },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/payroll', label: 'Payroll', icon: Wallet },
    { path: '/invoice', label: 'Invoice Generator', icon: FileText },
    { path: '/password-logs', label: 'Password Logs', icon: History },
    { path: '/activity', label: 'Activity Logs', icon: Activity },
  ];

  const managerLinks = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/today-sales', label: 'Today Sales', icon: TrendingUp },
    { path: '/data', label: 'Upload Data', icon: Database },
    { path: '/search', label: 'Search Records', icon: Search },
    { path: '/chat', label: 'Chat', icon: MessageCircle },
    { path: '/attendance', label: 'Attendance', icon: CalendarCheck },
    { path: '/holidays', label: 'Holidays', icon: CalendarOff },
    { path: '/my-payslips', label: 'My Payslips', icon: Wallet },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  const agentLinks = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/today-sales', label: 'Today Sales', icon: TrendingUp },
    { path: '/search', label: 'Search Records', icon: Search },
    { path: '/chat', label: 'Chat', icon: MessageCircle },
    { path: '/attendance', label: 'Attendance', icon: CalendarCheck },
    { path: '/my-payslips', label: 'My Payslips', icon: Wallet },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  const links = user?.role === 'admin' ? adminLinks : user?.role === 'manager' ? managerLinks : agentLinks;

  return (
    <aside className="w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col shadow-2xl" data-testid="sidebar">
      <div className="p-6 border-b border-slate-700">
        <div className="flex items-center gap-3 mb-2">
          <img 
            src={LOGO_URL} 
            alt="Creative Infotech Logo" 
            className="w-12 h-12 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg items-center justify-center shadow-lg hidden">
            <span className="text-white font-bold text-xl">CI</span>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Creative Infotech</h2>
            <p className="text-xs text-slate-400">
              {user.role === 'admin' ? 'Admin Portal' : user.role === 'manager' ? 'Manager Portal' : 'Agent Portal'}
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {links.map((link) => {
          const Icon = link.icon;
          const isActive = location.pathname === link.path;
          const isChatLink = link.path === '/chat';
          return (
            <Link key={link.path} to={link.path}>
              <Button
                variant={isActive ? 'default' : 'ghost'}
                className={cn(
                  'w-full justify-start text-left transition-all duration-200 relative',
                  isActive 
                    ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-lg shadow-blue-500/30' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                )}
                data-testid={`nav-${link.path}`}
              >
                <Icon className="mr-3 h-5 w-5" strokeWidth={2} />
                {link.label}
                {isChatLink && unreadChat > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1.5 animate-pulse"
                    data-testid="chat-unread-badge">
                    {unreadChat > 99 ? '99+' : unreadChat}
                  </span>
                )}
              </Button>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-700">
        <div className="mb-4 p-4 bg-slate-800 rounded-lg border border-slate-700">
          <p className="text-xs font-medium text-slate-400 mb-1">Signed in as</p>
          <p className="text-sm font-semibold truncate">{user.agent_name}</p>
          <p className="text-xs text-slate-400 truncate">{user.email}</p>
          <div className="mt-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
            {user.role === 'admin' ? '👑 Administrator' : user.role === 'manager' ? '💼 Manager' : '👤 Agent'}
          </div>
        </div>
        <div className="space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white transition-all duration-200"
            onClick={() => setChangePasswordOpen(true)}
            data-testid="change-password-button"
          >
            <KeyRound className="mr-3 h-4 w-4" strokeWidth={2} />
            Change Password
          </Button>
          {isDesktopApp && (
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white transition-all duration-200"
              onClick={() => setServerConfigOpen(true)}
              data-testid="server-config-button"
            >
              <Server className="mr-3 h-4 w-4" strokeWidth={2} />
              Server Settings
            </Button>
          )}
          <Button
            variant="outline"
            className="w-full justify-start border-slate-700 bg-slate-800 hover:bg-red-600 hover:border-red-600 hover:text-white text-slate-300 transition-all duration-200"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-3 h-4 w-4" strokeWidth={2} />
            Sign Out
          </Button>
        </div>
      </div>
      
      <ChangePasswordDialog 
        open={changePasswordOpen} 
        onOpenChange={setChangePasswordOpen} 
      />
      <ServerConfigDialog 
        open={serverConfigOpen} 
        onOpenChange={setServerConfigOpen} 
      />
    </aside>
  );
};

export default Sidebar;